import React from "react";
import { Link } from "react-router-dom";
import { gbp, resolveImageUrl } from "@/lib/api";
import WishlistButton from "@/components/WishlistButton";

// Tiny CSS-color hint table for known colour names so the dot looks right
// even when the product doesn't supply a hex. Falls back to a neutral grey.
const COLOR_HEX = {
  black: "#0a0a0a", white: "#ffffff", grey: "#999999", gray: "#999999",
  charcoal: "#3a3a3a", navy: "#1a2a4a", "navy blue": "#1a2a4a",
  red: "#c8102e", maroon: "#7A0A1E", burgundy: "#5b1320",
  blue: "#1e4fc4", "royal blue": "#1e4fc4", "sky blue": "#7ec9e8",
  green: "#1d7a3b", olive: "#6a6e2a", forest: "#1d4a2a",
  yellow: "#f3c200", mustard: "#d4a01a", gold: "#c9a227",
  orange: "#ff7a2a", brown: "#5b3a22", tan: "#c2a47e", cream: "#f5e9d3",
  pink: "#f2a3b4", purple: "#5b2a8a", lilac: "#a99cc6",
  silver: "#c4c4c4",
};
const colorHex = (name) => {
  if (!name) return "#cccccc";
  const key = String(name).trim().toLowerCase();
  return COLOR_HEX[key] || "#cccccc";
};

export default function ProductCard({ p }) {
  const discounted = p.discount_price && p.discount_price < p.price;
  const stock = p.stock ?? 999;
  const low = p.low_stock_threshold ?? 5;
  const isSoldOut = stock <= 0;
  const isLowStock = !isSoldOut && stock <= low;
  const colors = Array.isArray(p.colors) ? p.colors.filter(Boolean).slice(0, 5) : [];

  // Wrap helper: title+image go to /product/:id; colour swatches deep-link
  // to /product/:id?color=<name> so the PDP can pre-select the variant.
  const productHref = `/product/${p.id}`;

  return (
    <div className="group block" data-testid={`product-card-${p.id}`}>
      <Link to={productHref} className="block">
      <div className="relative overflow-hidden bg-[#FAFAFA] aspect-[4/5] hairline">
        <WishlistButton productId={p.id} variant="card" />
        <img
          src={resolveImageUrl(p.image_url || (p.images || []).find(Boolean) || "")}
          alt={p.title}
          loading="lazy"
          onError={(e) => {
            // Fallback chain: image_url → next valid entry in images[] → hide.
            // We never substitute the MotoWear logo as a placeholder — a logo
            // looks like a real product card and confuses customers.
            const img = e.currentTarget;
            const tried = new Set((img.dataset.tried || "").split("|").filter(Boolean));
            tried.add(img.src);
            const candidates = (p.images || [])
              .map(resolveImageUrl)
              .filter((u) => u && !tried.has(u));
            if (candidates.length > 0) {
              tried.add(candidates[0]);
              img.dataset.tried = Array.from(tried).join("|");
              img.src = candidates[0];
              return;
            }
            // No more candidates — hide the image entirely (the card still
            // renders, just on a flat neutral background).
            img.style.visibility = "hidden";
          }}
          className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] ${isSoldOut ? "opacity-50" : ""}`}
        />
        {discounted && !isSoldOut && !p.sale_applied && !p.bf_applied && (
          <span className="absolute top-3 left-3 bg-moto-gradient text-white label-eyebrow px-3 py-1.5 shadow-[0_4px_12px_rgba(255,59,48,0.35)]" data-testid={`product-sale-badge-${p.id}`}>
            Sale
          </span>
        )}
        {(p.sale_applied || p.bf_applied) && (
          <span
            className="absolute top-3 left-3 label-eyebrow px-3 py-1.5 shadow-[0_4px_12px_rgba(0,0,0,0.18)]"
            style={{
              backgroundColor: p.sale_badge_bg || "#FFD400",
              color: p.sale_badge_fg || "#000000",
            }}
            data-testid={`product-sale-tag-${p.id}`}
          >
            {(p.sale_label || "Black Friday")} · -{Math.round(p.sale_percent || p.bf_percent || 0)}%
          </span>
        )}
        {p.going_fast && !isSoldOut && (
          <span
            className="absolute bottom-3 left-3 label-eyebrow text-[10px] px-2.5 py-1 bg-black text-white shadow-[0_4px_12px_rgba(0,0,0,0.25)] flex items-center gap-1.5"
            data-testid={`product-going-fast-${p.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
            Going Fast · {p.going_fast_remaining} left
          </span>
        )}
        {isSoldOut && (
          <span className="absolute top-3 right-3 bg-black text-white label-eyebrow px-3 py-1.5" data-testid={`product-soldout-badge-${p.id}`}>
            Sold Out
          </span>
        )}
        {isLowStock && (
          <span className="absolute top-3 right-3 bg-[#FF3B30] text-white label-eyebrow px-3 py-1.5 shadow-[0_4px_12px_rgba(255,59,48,0.35)]" data-testid={`product-lowstock-badge-${p.id}`}>
            Almost Sold Out
          </span>
        )}
      </div>
      </Link>
      {colors.length > 0 && (
        <div
          className="mt-3 flex items-center gap-2"
          data-testid={`product-colors-${p.id}`}
          title={colors.join(" · ")}
        >
          <span className="label-eyebrow text-[10px] text-black/45 mr-1">Colour</span>
          {colors.map((c) => (
            <Link
              key={c}
              to={`${productHref}?color=${encodeURIComponent(c)}`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Choose ${c}`}
              className="relative w-5 h-5 rounded-full border border-black/15 hover:scale-110 hover:border-black transition-transform"
              style={{ backgroundColor: colorHex(c) }}
              data-testid={`product-color-dot-${p.id}-${c.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
        </div>
      )}
      <Link to={productHref} className="block">
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow text-black/40">{p.theme_slug} · {p.type_slug}</div>
          <h3 className="font-display text-xl mt-1 tracking-tight" data-testid={`product-title-${p.id}`}>{p.title}</h3>
        </div>
        <div className="text-right">
          {(p.sale_applied || p.bf_applied) ? (
            <>
              <div className="text-sm text-black/40 line-through" data-testid={`product-sale-original-${p.id}`}>{gbp(p.discount_price || p.price)}</div>
              <div className="text-base font-medium" style={{ color: "#b88a00" }} data-testid={`product-sale-price-${p.id}`}>{gbp(p.sale_price ?? p.bf_price)}</div>
            </>
          ) : discounted ? (
            <>
              <div className="text-sm text-black/40 line-through">{gbp(p.price)}</div>
              <div className="text-base font-medium moto-accent">{gbp(p.discount_price)}</div>
            </>
          ) : (
            <div className="text-base font-medium">{gbp(p.price)}</div>
          )}
        </div>
      </div>
      </Link>
    </div>
  );
}
